import React, { memo, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Typography,
} from "@material-tailwind/react";

import {
  BsCalculator,
  BsCopy,
  BsCheck,
} from "react-icons/bs";

import persianNumberFormat from "../../helper/PersianNumberFormat";

const VAT_PERCENTAGE = 10;
const SITE_COMMISSION_PERCENTAGE = 40;

const calculateBackendShares = (servicePrice) => {
  const normalizedPrice = Math.max(0, Math.floor(servicePrice));
  const siteShare = Math.floor(normalizedPrice * SITE_COMMISSION_PERCENTAGE / 100);
  const agentShare = normalizedPrice - siteShare;
  const vatShare = Math.ceil(normalizedPrice * VAT_PERCENTAGE / 100);

  return {
    totalPrice: normalizedPrice + vatShare,
    vatShare,
    agentShare,
    siteShare,
    serviceShare: normalizedPrice,
  };
};

const findServicePriceByTotal = (totalPrice) => {
  let low = 0;
  let high = Math.max(0, Math.floor(totalPrice));

  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (calculateBackendShares(middle).totalPrice < totalPrice) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  if (low === 0) return low;

  const lowerDifference = Math.abs(calculateBackendShares(low - 1).totalPrice - totalPrice);
  const upperDifference = Math.abs(calculateBackendShares(low).totalPrice - totalPrice);
  return lowerDifference <= upperDifference ? low - 1 : low;
};

const findServicePriceByAgentShare = (targetAgentShare) => {
  let low = 0;
  let high = Math.ceil(targetAgentShare * 100 / (100 - SITE_COMMISSION_PERCENTAGE)) + 1;

  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (calculateBackendShares(middle).agentShare < targetAgentShare) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
};

/* -------------------------------- */
/* Tailwind SAFE Color Mapping */
/* -------------------------------- */
const colorStyles = {
  blue: {
    bg: "bg-blue-100",
    border: "border-blue-200",
    text: "text-blue-600",
  },
  red: {
    bg: "bg-red-100",
    border: "border-red-200",
    text: "text-red-600",
  },
  green: {
    bg: "bg-green-100",
    border: "border-green-200",
    text: "text-green-600",
  },
  purple: {
    bg: "bg-purple-100",
    border: "border-purple-200",
    text: "text-purple-600",
  },
  premium: {
    bg: "bg-gradient-to-br from-blue-gray-900 via-blue-gray-800 to-blue-gray-700",
    border: "border-blue-gray-700",
    text: "text-blue-gray-50",
    label: "!text-blue-gray-100",
  },
};

/* -------------------------------- */
/* Result Card Component */
/* -------------------------------- */
const ResultCard = ({
  label,
  value,
  color,
  field,
  copyFn,
  copiedField,
}) => {
  const styles = colorStyles[color];
  const isCopied = copiedField === field;
  const copyValue = () => copyFn(value.toFixed(0), field);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={copyValue}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          copyValue();
        }
      }}
      className={`p-3 ${styles.bg} rounded-lg border ${styles.border}
      hover:shadow-md transition-all group cursor-pointer
      ${isCopied ? "ring-1 ring-blue-gray-300 ring-offset-1" : ""}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <Typography
            variant="tiny"
            color="gray"
            className={`mb-1 ${styles.label || ""}`}
          >
            {label}
          </Typography>

          <Typography
            variant="small"
            className={`font-bold ${styles.text}`}
          >
            {persianNumberFormat(value.toFixed(0), true)}
          </Typography>
        </div>

        <div className="flex items-center gap-1">
          {isCopied && (
            <Typography
              variant="tiny"
              className="text-[10px] font-medium tracking-normal text-blue-gray-500"
            >
              کپی شد
            </Typography>
          )}

          <span
            className={`transition-opacity p-1 rounded ${
              isCopied
                ? "opacity-100 bg-blue-gray-50/80"
                : "opacity-0 group-hover:opacity-100 hover:bg-white/50"
            }`}
          >
            {isCopied ? (
              <BsCheck className="text-blue-gray-600" />
            ) : (
              <BsCopy className={styles.text} />
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------- */
/* Main Component */
/* -------------------------------- */
const AgentServiceShareCalculator = () => {
  const [totalPrice, setTotalPrice] = useState("");
  const [agentShare, setAgentShare] = useState("");
  const [activeInput, setActiveInput] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const [results, setResults] = useState({
    totalPrice: 0,
    vatShare: 0,
    agentShare: 0,
    siteShare: 0,
    serviceShare: 0,
  });

  /* ---------- Calculations ---------- */

  const calculateFromTotalPrice = (total) => {
    const totalNum = Number(total);

    if (!total || !Number.isSafeInteger(totalNum) || totalNum < 0) {
      resetResults();
      return;
    }

    setResults(calculateBackendShares(findServicePriceByTotal(totalNum)));
  };

  const calculateFromAgentShare = (agent) => {
    const agentNum = Number(agent);

    if (!agent || !Number.isSafeInteger(agentNum) || agentNum < 0) {
      resetResults();
      return;
    }

    setResults(calculateBackendShares(findServicePriceByAgentShare(agentNum)));
  };

  const resetResults = () => {
    setResults({
      totalPrice: 0,
      vatShare: 0,
      agentShare: 0,
      siteShare: 0,
      serviceShare: 0,
    });
  };

  /* ---------- Handlers ---------- */

  const handleTotalPriceChange = (e) => {
    const val = e.target.value;
    setTotalPrice(val);
    calculateFromTotalPrice(val);
  };

  const handleAgentShareChange = (e) => {
    const val = e.target.value;
    setAgentShare(val);
    calculateFromAgentShare(val);
  };

  const resetAndFocus = (type) => {
    setActiveInput(type);

    if (type === "totalPrice") setAgentShare("");
    else setTotalPrice("");

    resetResults();
  };

  const copyToClipboard = (value, fieldName) => {
    navigator.clipboard.writeText(value.toString());

    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  /* ---------- UI ---------- */

  return (
    <Card className="w-full shadow-md border border-blue-gray-100">
      <CardBody className="w-full p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-blue-gray-200">
          <BsCalculator className="h-5 w-5 text-blue-500" />
          <Typography variant="h6" color="blue-gray" className="font-bold">
            محاسبه‌گر درآمد
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Inputs */}
          <div className="md:col-span-1 space-y-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <Typography
              variant="small"
              color="blue-gray"
              className="font-bold"
            >
              📥 ورودی
            </Typography>

            {/* Total */}
            <div className="flex flex-col gap-2">
              <Typography className="font-semibold text-blue-900 text-xs">
                کل مبلغ
              </Typography>

              <Input
                value={totalPrice}
                onChange={handleTotalPriceChange}
                onFocus={() => resetAndFocus("totalPrice")}
                placeholder="1100000"
                className={`border text-sm py-1 ${
                  activeInput === "agentShare"
                    ? "border-gray-300 bg-gray-100 text-gray-500"
                    : "border-blue-400 bg-white"
                }`}
              />
            </div>

            {/* Divider */}
            <div className="flex items-center gap-2">
              <div className="h-0.5 flex-1 bg-gray-300"></div>
              <Typography variant="tiny">یا</Typography>
              <div className="h-0.5 flex-1 bg-gray-300"></div>
            </div>

            {/* Agent */}
            <div className="flex flex-col gap-2">
              <Typography className="font-semibold text-blue-900 text-xs">
                سهم مشاور
              </Typography>

              <Input
                value={agentShare}
                onChange={handleAgentShareChange}
                onFocus={() => resetAndFocus("agentShare")}
                placeholder="600000"
                className={`border text-sm py-1 ${
                  activeInput === "totalPrice"
                    ? "border-gray-300 bg-gray-100 text-gray-500"
                    : "border-green-400 bg-white"
                }`}
              />
            </div>
          </div>

          {/* Results */}
          <div className="md:col-span-2 space-y-2">
            <Typography
              variant="small"
              color="blue-gray"
              className="font-bold px-1"
            >
              📊 نتایج
            </Typography>

            <div className="grid grid-cols-2 gap-2">
              <ResultCard
                label="کل مبلغ"
                value={results.totalPrice}
                color="blue"
                field="totalPrice"
                copyFn={copyToClipboard}
                copiedField={copiedField}
              />

              <ResultCard
                label={`مالیات (${VAT_PERCENTAGE}%)`}
                value={results.vatShare}
                color="red"
                field="vatShare"
                copyFn={copyToClipboard}
                copiedField={copiedField}
              />

              <ResultCard
                label={`سهم مشاور (${100 - SITE_COMMISSION_PERCENTAGE}%)`}
                value={results.agentShare}
                color="green"
                field="agentShare"
                copyFn={copyToClipboard}
                copiedField={copiedField}
              />

              <ResultCard
                label={`سهم مباشر (${SITE_COMMISSION_PERCENTAGE}%)`}
                value={results.siteShare}
                color="purple"
                field="siteShare"
                copyFn={copyToClipboard}
                copiedField={copiedField}
              />

              <ResultCard
                label="مجموع سهم مباشر و مشاور"
                value={results.serviceShare}
                color="premium"
                field="serviceShare"
                copyFn={copyToClipboard}
                copiedField={copiedField}
              />
            </div>
          </div>
        </div>

        {/* Formula */}
        <div className="mt-4 p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg text-white text-xs font-semibold space-y-1">
          <div>📐 قیمت کل = قیمت خدمت + مالیاتِ رو به بالا گرد شده</div>
          <div>💵 مالیات = {VAT_PERCENTAGE}٪ قیمت خدمت</div>
          <div>👤 سهم مشاور = قیمت خدمت منهای سهم مباشر</div>
          <div>🏢 سهم مباشر = {SITE_COMMISSION_PERCENTAGE}٪ قیمت خدمت، بدون اعشار</div>
          <div>🤝 مجموع سهم مباشر و مشاور = قیمت خدمت</div>
        </div>
      </CardBody>
    </Card>
  );
};

export default memo(AgentServiceShareCalculator);
